import { toValue, computed, ref, watchEffect } from 'vue'
import _debounce from 'lodash/debounce'
import { useSearchStore } from '@/stores/search'
import type {
  CheckboxFilter,
  CheckboxFilterItem,
  SearchTag,
  TriStateCheckboxState,
} from '@/types/search-types'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'

type TriStateCheckboxFilterParams = {
  checkboxFilter: MaybeRefOrGetter<CheckboxFilter | null> | ComputedRef<CheckboxFilter | null>
  triStateEnabled: () => boolean
  debounceCallback: (checkboxItems: SearchTag[]) => any
}

/**
 * Composable w/ helpers for tri-state checkbox filters
 * [x] tri-state restricted to plus users
 * [x] accessibility / aria-label changes
 * [x] manually manage state via data-state attribute (tri-state is not native html)
 * [x] styling based on data-state attribute
 * [x] debounce checkbox changes for list
 * [x] toggle all checkbox items
 */
export function useTriStateCheckboxFilter({
  checkboxFilter,
  triStateEnabled,
  debounceCallback,
}: TriStateCheckboxFilterParams) {
  const searchStore = useSearchStore()
  const debounceItemIds = new Set<string>()

  const checkboxAll = ref<CheckboxFilterItem>({
    counter: 0,
    filterName: toValue(checkboxFilter)?.name || '',
    id: `${toValue(checkboxFilter)?.name || ''}-all`,
    label: 'Toggle All Options',
    tagLabel: '',
    type: 'checkbox',
    value: '',
    state: '',
  })

  // clear out the all checkbox if no values are selected
  watchEffect(() => {
    const hasAnyChecked = toValue(checkboxFilter)?.items.some((item) => item.state !== '')

    if (!hasAnyChecked) {
      checkboxAll.value.state = ''
    }
  })

  /**
   * Read only proxy
   */
  const enableFacets = computed(() => searchStore.enableFacets)
  const allowEmptyFacets = computed(() => searchStore.allowEmptyFacets)

  /**
   * Aria-label to handle include vs exclude
   */
  function accessibilityLabel(item: CheckboxFilterItem): string {
    // past tense state if set
    const pastTenseState = item.state !== '' ? ` ${item.state}d` : ''

    return `${item.label}${pastTenseState} (${item.counter})`
  }

  /**
   * Two states for "checked"
   */
  function isChecked(itemState: TriStateCheckboxState): boolean {
    return itemState === 'include' || itemState === 'exclude'
  }

  /**
   * Disable checkboxes based on count
   *
   * allowEmptyFacets allows the checkbox to stay enabled even if there are no items. Currently in
   * use for Notification Filters where we don't want to disable checkboxes based on count/other filters.
   */
  function checkboxDisabled(itemCount: number): boolean {
    return searchStore.enableFacets && !searchStore.allowEmptyFacets && itemCount === 0
  }

  /**
   * Checkbox @change handler to support tri-state if FB Plus or
   * standard two-state if free member
   */
  function checkboxChange(item: CheckboxFilterItem): void {
    // need to update html data-state attribute for styling and manually
    // managed .checked property w/ tri-state
    const checkboxEl = document.querySelector(`#${item.id}`)

    // rotates through 'included' | 'excluded' | ''
    let nextState: TriStateCheckboxState = ''

    if (toValue(triStateEnabled)) {
      // tri-state checkbox
      if (item.state === '') {
        nextState = 'include'
      } else if (item.state === 'include') {
        nextState = 'exclude'
      } else if (item.state === 'exclude') {
        nextState = ''
      }
    } else {
      // two state checkbox
      nextState = item.state === '' ? 'include' : ''
    }

    // update store
    searchStore.setCheckboxState({
      filterName: item.filterName,
      id: item.id,
      state: nextState,
    })

    // standard input:checked auto toggles for two states
    // manually manage :checked for tri-state
    if (checkboxEl instanceof HTMLInputElement) {
      checkboxEl.checked = isChecked(nextState)
    }

    // debounce the change
    debounceItemIds.add(item.id)
    debounceChange()
  }

  /**
   * Toggle all include / exclude / '' for an entire checkbox filter.
   */
  function checkboxChangeAll(item: CheckboxFilterItem) {
    const filters = toValue(checkboxFilter)
    const checkboxAllEl = document.querySelector(`#${item.id}`)

    if (!filters) {
      return
    }

    // rotates through 'included' | 'excluded' | ''
    let nextState: TriStateCheckboxState = ''

    if (toValue(triStateEnabled)) {
      // tri-state checkbox
      if (item.state === '') {
        nextState = 'include'
      } else if (item.state === 'include') {
        nextState = 'exclude'
      } else if (item.state === 'exclude') {
        nextState = ''
      }
    } else {
      // two state checkbox
      nextState = item.state === '' ? 'include' : ''
    }

    // mutate, should be checkboxAll that was passed in
    item.state = nextState

    // standard input:checked auto toggles for two states
    // manually manage :checked for tri-state
    if (checkboxAllEl instanceof HTMLInputElement) {
      checkboxAllEl.checked = isChecked(nextState)
    }

    // toggle all filters
    filters.items.forEach((item) => {
      searchStore.setCheckboxState({
        filterName: item.filterName,
        id: item.id,
        state: nextState,
      })

      // standard input:checked auto toggles for two states
      // manually manage :checked for tri-state
      const checkboxEl = document.querySelector(`#${item.id}`)
      if (checkboxEl instanceof HTMLInputElement) {
        checkboxEl.checked = isChecked(nextState)
      }

      debounceItemIds.add(item.id)
    })
    // end toggle all

    debounceChange()
  }

  /**
   * Debounce then emit all the checkbox items that were touched
   */
  const debounceChange = _debounce(() => {
    const filters = toValue(checkboxFilter)
    let checkboxTags: SearchTag[] = []

    if (filters) {
      // touched checkbox items, but also SearchTags schema
      checkboxTags = filters.items
        .filter((item) => debounceItemIds.has(item.id))
        .map((checkboxItem) => searchStore.checkboxTagItem(checkboxItem))
      debounceItemIds.clear()
    }

    debounceCallback(checkboxTags)
  }, 500)

  return {
    checkboxAll,
    accessibilityLabel,
    isChecked,
    checkboxDisabled,
    checkboxChange,
    checkboxChangeAll,
    enableFacets,
    allowEmptyFacets,
  }
}
